"use client";

import { useAuth } from "@clerk/nextjs";
import { Star } from "lucide-react";
import {
  startTransition,
  useActionState,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import {
  type ToggleFavoriteActionState,
  toggleFavoriteAction,
} from "~/server/actions/videos-users/actions";

export default function Controls({
  videoId,
  defaultIsFavorite,
  className,
}: {
  videoId: number;
  defaultIsFavorite: boolean;
  className?: string;
}) {
  const [state, formAction] = useActionState<
    ToggleFavoriteActionState,
    FormData
  >(toggleFavoriteAction, { message: "" });

  const { isSignedIn } = useAuth();

  const [isFavorite, setIsFavorite] = useState(defaultIsFavorite);
  const hasSubmitted = useRef(false);

  useEffect(() => {
    if (!hasSubmitted.current) return;
    if (!state.success) {
      setIsFavorite(state.isFavorite ?? defaultIsFavorite);
    }
  }, [state, defaultIsFavorite]);

  const handleToggleFavorite = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsFavorite(!isFavorite);
    hasSubmitted.current = true;
    startTransition(() => {
      formAction(new FormData(event.currentTarget));
    });
  };

  return (
    <form onSubmit={handleToggleFavorite} className={cn("contents", className)}>
      <input type="hidden" name="videoId" value={videoId} />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="submit"
            size="icon"
            variant="ghost"
            disabled={!isSignedIn}
            aria-label={
              isFavorite ? "Remove from favorites" : "Add to favorites"
            }
          >
            <Star className={cn("h-4 w-4", { "fill-current": isFavorite })} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isFavorite ? "Remove from favorites" : "Add to favorites"}
        </TooltipContent>
      </Tooltip>
    </form>
  );
}
